import {
  MAX_BEDS_BATHS,
  MAX_DESCRIPTION,
  MAX_NIGHTS,
  MAX_PROPERTY_PRICE,
  MAX_TITLE,
  MAX_UNITS,
  MIN_UNITS,
  SAUDI_COUNTRY,
} from "./constants";

export const getPhoneDigits = (value) => String(value || "").replace(/\D/g, "");

export const parseLatLng = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  const match = raw.match(
    /^\s*(-?\d+(?:\.\d+)?)\s*[,/\s]\s*(-?\d+(?:\.\d+)?)\s*$/,
  );
  if (!match) return null;
  const lat = Number(match[1]);
  const lng = Number(match[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

export const parseCheckInTimeToHours = (label) => {
  if (!label) return -1;
  if (String(label).includes("noon")) return 12;
  if (String(label).includes("midnight")) return 0;
  const match = String(label).match(/(\d+):\d+\s?(AM|PM)/i);
  if (!match) return -1;
  let hour = parseInt(match[1], 10);
  const period = match[2].toUpperCase();
  if (period === "PM" && hour !== 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return hour;
};

/** Clientside rule: checkout hour must be ≤ check-in hour − 2. */
export const isCheckInWindowValid = (from, to) => {
  const fromH = parseCheckInTimeToHours(from);
  const toH = parseCheckInTimeToHours(to);
  if (fromH < 0 || toH < 0) return false;
  return toH <= fromH - 2;
};

const asNumber = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return undefined;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

const asInt = (value) => {
  const n = asNumber(value);
  if (n === undefined || !Number.isInteger(n)) return undefined;
  return n;
};

/**
 * Returns human-readable issue labels for the card status chip.
 */
export const getListingIssues = (listing) => {
  const issues = [];
  const digits = getPhoneDigits(listing.mobileNumber);
  if (digits.length < 9) issues.push("Mobile number");
  else if (!listing.accountOk && !listing.userId) issues.push("Account");

  if (!listing.propertyType) issues.push("Property type");
  if (!listing.roomType) issues.push("Room type");

  const units = asInt(listing.totalUnits);
  if (units === undefined || units < MIN_UNITS || units > MAX_UNITS) {
    issues.push("Total units");
  }

  const roomAmount = asInt(listing.roomAmount);
  if (roomAmount === undefined || roomAmount < 1 || roomAmount > 10) {
    issues.push("Room amount");
  }

  const guests = asInt(listing.guests);
  if (guests === undefined || guests < 1) issues.push("Guests");

  const bedrooms = asInt(listing.bedrooms);
  if (bedrooms === undefined || bedrooms < 0) issues.push("Bedrooms");

  const bedsTotal = asInt(listing.bedsTotal);
  if (
    bedsTotal === undefined ||
    bedsTotal < 1 ||
    bedsTotal > MAX_BEDS_BATHS
  ) {
    issues.push("Total beds");
  } else {
    const types = listing.bedTypes || [];
    if (
      types.length !== bedsTotal ||
      types.some((t) => !String(t || "").trim())
    ) {
      issues.push("Bed types");
    }
  }

  const bathroomsTotal = asInt(listing.bathroomsTotal);
  if (
    bathroomsTotal === undefined ||
    bathroomsTotal < 1 ||
    bathroomsTotal > MAX_BEDS_BATHS
  ) {
    issues.push("Total bathrooms");
  } else {
    const types = listing.bathroomTypes || [];
    if (
      types.length !== bathroomsTotal ||
      types.some((t) => !String(t || "").trim())
    ) {
      issues.push("Bathroom types");
    }
  }

  if (!listing.citySelected || !listing.cityEn || !listing.cityAr) {
    issues.push("City");
  }
  if (!String(listing.street || "").trim()) issues.push("Street");
  if (!String(listing.state || "").trim()) issues.push("District / state");
  if (!String(listing.zip || "").trim()) issues.push("ZIP");
  if (!parseLatLng(listing.latlng)) issues.push("Map pin");

  const title = String(listing.title || "").trim();
  if (!title) issues.push("Title");
  else if (title.length > MAX_TITLE) issues.push("Title too long");

  const description = String(listing.description || "").trim();
  if (!description) issues.push("Description");
  else if (description.length > MAX_DESCRIPTION) {
    issues.push("Description too long");
  }

  if (!listing.cancellationPolicy) issues.push("Cancellation policy");
  if (!listing.checkInFrom || !listing.checkInTo) issues.push("Check-in times");
  else if (!isCheckInWindowValid(listing.checkInFrom, listing.checkInTo)) {
    issues.push("Check-in window");
  }

  const basePrice = asNumber(listing.basePrice);
  if (
    basePrice === undefined ||
    basePrice <= 0 ||
    basePrice > MAX_PROPERTY_PRICE
  ) {
    issues.push("Base price");
  }

  const taxes = asNumber(listing.taxes);
  if (taxes === undefined || taxes < 0 || taxes > 100) issues.push("Taxes");

  for (const [key, label] of [
    ["weeklyDiscount", "Weekly discount"],
    ["monthlyDiscount", "Monthly discount"],
  ]) {
    const d = asNumber(listing[key]);
    if (d === undefined || d < 0 || d > 100) issues.push(label);
  }

  if (!listing.availabilityWindow) issues.push("Availability window");

  const minNights = asInt(listing.minNights);
  const maxNights = asInt(listing.maxNights);
  if (
    minNights === undefined ||
    minNights < 1 ||
    minNights > MAX_NIGHTS
  ) {
    issues.push("Minimum nights");
  }
  if (
    maxNights === undefined ||
    maxNights < 1 ||
    maxNights > MAX_NIGHTS
  ) {
    issues.push("Maximum nights");
  } else if (minNights !== undefined && minNights > maxNights) {
    issues.push("Nights range");
  }

  if (listing.securityDepositEnabled) {
    const amount = asNumber(listing.securityDepositAmount);
    if (amount === undefined || amount < 0) issues.push("Deposit amount");
  }

  const weekendRaw = String(listing.weekendPrice ?? "").trim();
  if (weekendRaw) {
    const weekendPrice = asNumber(listing.weekendPrice);
    if (
      weekendPrice === undefined ||
      weekendPrice <= 0 ||
      weekendPrice > MAX_PROPERTY_PRICE
    ) {
      issues.push("Weekend price");
    }
  }

  return issues;
};

export const isListingReady = (listing) => getListingIssues(listing).length === 0;

/**
 * Map a form listing to the bulk-create API row shape.
 * rowNumber is 2-based (legacy Excel convention) for photo key matching.
 */
export const toApiRow = (listing, rowNumber) => {
  const coords = parseLatLng(listing.latlng);
  const basePrice = asNumber(listing.basePrice);
  const weekendRaw = String(listing.weekendPrice ?? "").trim();
  const weekendPrice = weekendRaw
    ? asNumber(listing.weekendPrice)
    : basePrice;

  return {
    rowNumber,
    mobileNumber: String(listing.mobileNumber || "").trim(),
    userId: String(listing.userId || "").trim() || undefined,
    totalUnits: asInt(listing.totalUnits),
    propertyType: listing.propertyType,
    roomType: listing.roomType,
    roomAmount: asInt(listing.roomAmount),
    personalHome: Boolean(listing.personalHome),
    guests: asInt(listing.guests),
    bedrooms: asInt(listing.bedrooms),
    bedsTotal: asInt(listing.bedsTotal),
    bedTypes: (listing.bedTypes || []).filter(Boolean),
    bathroomsTotal: asInt(listing.bathroomsTotal),
    bathroomTypes: (listing.bathroomTypes || []).filter(Boolean),
    countryEn: SAUDI_COUNTRY.en,
    countryAr: SAUDI_COUNTRY.ar,
    street: String(listing.street || "").trim(),
    apt: String(listing.apt || "").trim(),
    cityEn: String(listing.cityEn || "").trim(),
    cityAr: String(listing.cityAr || "").trim(),
    state: String(listing.state || "").trim(),
    zip: String(listing.zip || "").trim(),
    latitudes: coords ? String(coords.lat) : "",
    longitudes: coords ? String(coords.lng) : "",
    essentials: listing.essentials || [],
    safeties: listing.safeties || [],
    spaces: listing.spaces || [],
    propertyRules: listing.propertyRules || [],
    title: String(listing.title || "").trim(),
    description: String(listing.description || "").trim(),
    checkInFrom: listing.checkInFrom,
    checkInTo: listing.checkInTo,
    cancellationPolicy: listing.cancellationPolicy,
    currency: "SAR",
    basePrice,
    taxes: asNumber(listing.taxes),
    thursdayPrice: weekendPrice,
    fridayPrice: weekendPrice,
    saturdayPrice: weekendPrice,
    weeklyDiscount: asNumber(listing.weeklyDiscount) ?? 0,
    monthlyDiscount: asNumber(listing.monthlyDiscount) ?? 0,
    availabilityWindow: listing.availabilityWindow || "allFutureDates",
    minNights: asInt(listing.minNights),
    maxNights: asInt(listing.maxNights),
    wifiName: String(listing.wifiName || "").trim(),
    wifiPassword: String(listing.wifiPassword || "").trim(),
    securityDepositEnabled: Boolean(listing.securityDepositEnabled),
    securityDepositAmount: listing.securityDepositEnabled
      ? asNumber(listing.securityDepositAmount) ?? 0
      : "",
    securityDepositRefundMethod: "cash",
    securityDepositNotes: String(listing.securityDepositNotes || "").trim(),
  };
};

export const listingsToApiRows = (listings) =>
  listings.map((listing, index) => toApiRow(listing, index + 2));
